import {appName, ADMIN_ACCOUNT_NAME} from '../settings/constants'
import {createSelector} from 'reselect'
import {Record} from 'immutable'
import RestService from '../helpers/RestService'
import {SET_LANGUAGES} from './app'
import { SubmissionError } from 'redux-form'
import {formatErrorMessage, middleThen, middleReduxFormThen} from "../helpers/common"
import {ajaxUrls, routesUrls} from '../settings/routes'
import {push} from "react-router-redux"
import {handleInitDonatePopup} from './app'
import {messages} from '../settings/messages'
import {toastr} from 'react-redux-toastr'

/**
 * Constants
 * */

export const moduleName = 'profile'
const prefix = `${appName}/${moduleName}`

export const UPLOAD_PROFILE_START = `${prefix}/UPLOAD_PROFILE_START`
export const UPLOAD_PROFILE_SUCCESS = `${prefix}/UPLOAD_PROFILE_SUCCESS`
export const SET_TEACHER_PERMISSION = `${prefix}/SET_TEACHER_PERMISSION`
export const UPLOAD_PROFILE_FAIL = `${prefix}/UPLOAD_PROFILE_FAIL`
export const UPDATE_PROFILE = `${prefix}/UPDATE_PROFILE`
export const CONFIRM_LOAD_START = `${prefix}/CONFIRM_LOAD_START`
export const CONFIRM_LOAD_SUCCESS = `${prefix}/CONFIRM_LOAD_SUCCESS`
export const CONFIRM_LOAD_FAIL = `${prefix}/CONFIRM_LOAD_FAIL`
export const CONFIRM_CLEAR = `${prefix}/CONFIRM_CLEAR`


/**
 * Reducer
 * */

export const profileRecord = Record({
    id : null,
    username : "",
    firstname : "",
    lastname : "",
    account : "",
    email : "",
    gender : null,
    language_id : null,
    payment_date_active : 0,
    is_organisation : 0,
    organisation_name : null,
    owner_id : null,
    created_at : null,
    updated_at : null,
})

export const loadStateRecord = Record({
    loading : false,
    loaded : false,
    error : "",
    success : "",
})

export const authLoadStateRecord = Record({
    confirm : new loadStateRecord(),
})

export const ReducerRecord = Record({
    entity : new profileRecord(),
    loading : false,
    loaded : false,
    permission : null,
    authLoadState : new authLoadStateRecord()
})

const defaultState = new ReducerRecord()

export default function reducer(state = defaultState, action) {
    const {type, payload} = action

    switch (type) {
        case UPLOAD_PROFILE_START : {
            return state.set('loading', true)
            break
        }
        case UPLOAD_PROFILE_SUCCESS : {
            let {profile} = payload
            return state.set('entity', new profileRecord({...profile}))
                        .set('loading', false)
                        .set('loaded', true)
            break
        }
        case SET_TEACHER_PERMISSION : {
            let {permission} = payload
            return state.set('permission', permission)
        }
        case UPLOAD_PROFILE_FAIL : {
            return state.set('loading', false)
            break
        }
        case UPDATE_PROFILE : {
            let {updateData} = payload
            return state.update('entity', profile => {
                for (let key in updateData) {
                    profile = profile.set(key, updateData[key])
                }
                return profile
            })
        }
        case CONFIRM_LOAD_START : {
            return state.setIn(['authLoadState', 'confirm', 'loading'], true)
            break
        }
        case CONFIRM_LOAD_SUCCESS : {
            return state.setIn(['authLoadState', 'confirm', 'loading'], false)
                        .setIn(['authLoadState', 'confirm', 'loaded'], true)
            break
        }
        case CONFIRM_LOAD_FAIL : {
            let {error = ""} = payload
            return state.setIn(['authLoadState', 'confirm', 'loading'], false)
                    .setIn(['authLoadState', 'confirm', 'loaded'], false)
                    .setIn(['authLoadState', 'confirm', 'error'], error)
        }
        case CONFIRM_CLEAR : {
            return state.updateIn(['authLoadState', 'reset'], () => new loadStateRecord())
            break
        }
        default : {
            return state
            break
        }
    }
}


/**
 * Selectors
 * */

export const stateSelector = state => state[moduleName]
export const profileSelector = createSelector(stateSelector, state => state.entity)
export const languageIdSelector = createSelector(profileSelector, profile => profile.language_id)
export const profileLoadStateSelector = createSelector(stateSelector, state => ({loaded : state.loaded, loading : state.loading}))
export const isAuthSelector = createSelector(stateSelector, state => !!state.entity.id)
export const isAdminSelector = createSelector(profileSelector, profile => profile.account === ADMIN_ACCOUNT_NAME)
export const isOrganisationSelector = createSelector(profileSelector, profile => profile.is_organisation == 1)
export const isTeacherOrganisationSelector = createSelector(profileSelector, profile => Boolean(profile.owner_id))

export const confirmLoadStateSelector = createSelector(stateSelector, state => ({
    loaded : state.authLoadState.confirm.loaded,
    loading : state.authLoadState.confirm.loading,
    error : state.authLoadState.confirm.error,
}))


/**
 * Action Creators
 * */

export const updateProfile = (updateData) => {
    return {
        type : UPDATE_PROFILE,
        payload : {
            updateData
        }
    }
}

export const confirmClear = () => ({
    type : CONFIRM_CLEAR,
})

export const confirmFail = params => {
    let {error} = params
    return {
        type : CONFIRM_LOAD_FAIL,
        payload : {
            error
        }
    }
}


/**
 * Thunk
 * */

export const successProfileResponse = ({dispatch, type, isReduxForm, toMainPage}) => response => {
    let {errcode, message, user, lang : languages = [], csrf, permission} = response

    if (errcode !== 200) {
        let _Error = isReduxForm ? SubmissionError : Error
        let _message = message || messages.standardError
        let _error_param = isReduxForm ? {_error : formatErrorMessage(_message)} : formatErrorMessage(_message)
        throw new _Error(_error_param)
        return
    }

    RestService.setCsrfToken(csrf)

    localStorage.removeItem('referalCode');

    dispatch({
        type : UPLOAD_PROFILE_SUCCESS,
        payload : {
            profile : {
                ...user,
                created_at : +user.created_at,
                updated_at : +user.updated_at,
            }
        }
    })
    dispatch({
        type : SET_LANGUAGES,
        payload : {
            languages,
        }
    })
    
    dispatch({
        type : SET_TEACHER_PERMISSION,
        payload : {
            permission,
        }
    })

    if (type) {
        dispatch({
            type
        })
    }

    if (toMainPage) dispatch(push(routesUrls.main))

    dispatch(handleInitDonatePopup())
}

export const uploadProfile = (resolve) => {
    return (dispatch) => {
        dispatch({
            type : UPLOAD_PROFILE_START,
        })
        RestService.customFetch(ajaxUrls.profileUserData)
            .then(res => {
                // if (res.status === 403) {
                //     //window.location = routesUrls.webLogin
                //     return
                // }
                if (res.status >= 400) throw new Error(res.statusText)
                return res.json()
            })
            .then(successProfileResponse({dispatch}))
            .catch(error => {
                console.warn(error) // TODO discuss with backend error handling
                dispatch({
                    type : UPLOAD_PROFILE_FAIL,
                })
            })
            .finally(() => {
                if (typeof resolve === 'function') resolve()
            })
    }
}

export const handleConfirm = params => (dispatch, getState) => {
    let {id, token} = params
    let state = getState()

    let {loaded, loading} = confirmLoadStateSelector(state)

    if (loaded || loading) return

    let data = new FormData()
    data.append('id', id)
    data.append('key', token)

    dispatch({
        type : CONFIRM_LOAD_START
    })
    RestService.bodyAsFormData(ajaxUrls.authConfirm, data)
        .then(middleThen)
        .then((res) => {
            successProfileResponse({dispatch, type:CONFIRM_LOAD_SUCCESS, toMainPage:true})(res);
            toastr.success('Email', 'Email verified');
        }).catch(error => {
            let _err = error.message ? formatErrorMessage(error.message) : formatErrorMessage(error)
            dispatch(confirmFail({
                error : _err
            }))
        })
}

/*
* Redux form
* */
export const handleProfileUpdateRequest = (params) => {
    let {firstname, lastname, gender, language_id, current_password, new_password, confirm_password} = params
    let requestParam = {
        firstname,
        lastname,
        language_id,
        gender,
    }
    if (current_password && new_password && confirm_password) requestParam = {...requestParam, current_password, new_password, confirm_password}

    return RestService.postFormData(ajaxUrls.profileUserUpdate, `user=${JSON.stringify(requestParam)}`)
        .then(res => {
            if (res.status >= 400) {
                throw new SubmissionError({
                    _error : res.statusText,
                })
            }
            return res.json()
        })
        .then(res => {
            let {errcode, message = ""} = res

            if (errcode === 200) {
                return {
                    firstname,
                    lastname,
                    language_id,
                    gender,
                }
            } else {
                throw new SubmissionError({
                    _error : formatErrorMessage(message),
                })
            }
        })
}

export const handleLoginRequest = params => {
    let {username, password, rememberMe = false} = params

    let data = new FormData()
    data.append('username', username)
    data.append('password', password)
    data.append('rememberMe', rememberMe ? "1" : "0")
    return RestService.bodyAsFormData(ajaxUrls.authLogin, data)
        .then(middleReduxFormThen)
}

export const handleSignupRequest = params => {
    let {username, firstname, lastname, email, gender, password, captcha, is_organisation, referal_code} = params
    let data = new FormData()
    data.append('username', username)
    data.append('firstname', firstname)
    data.append('lastname', lastname)
    data.append('email', email)
    data.append('gender', gender)
    data.append('password', password)
    data.append('verifyCode', captcha)
    data.append('referal_code', referal_code)
    data.append('is_organisation', is_organisation)

    return RestService.bodyAsFormData(ajaxUrls.authSignup, data)
            .then(middleReduxFormThen)
            .then(response => {
                let {errcode, message} = response

                if (errcode !== 200) {
                    // return Promise.reject(message)
                    throw new SubmissionError({
                        _error : formatErrorMessage(message),
                        message,
                    })
                    return
                }

                return response
            })
}

export const handleResetPasswordRequest = params => {
    let {email, captcha} = params

    let data = new FormData()
    data.append('email', email)
    data.append('verifyCode', captcha)

    return RestService.bodyAsFormData(ajaxUrls.authRequestResetPassword, data)
        .then(middleReduxFormThen)
        .then(response => {
            let {errcode, message} = response

            if (errcode !== 200) {
                throw new SubmissionError({
                    _error : formatErrorMessage(message),
                })
                return
            }

            return 'ok'
        })
}

export const handleResetPassword = params => {
    let {password, token} = params

    let data = new FormData()
    data.append('password', password)
    data.append('token', token)

    return RestService.bodyAsFormData(ajaxUrls.authResetPassword, data)
        .then(middleReduxFormThen)
        .then(response => {
            let {errcode, message} = response

            if (errcode !== 200) {
                throw new SubmissionError({
                    _error : formatErrorMessage(message),
                })
                return
            }

            return 'ok'
        })
}


// WEBPACK FOOTER //
// ./yii2/frontend/frontend-assets/src/modules/profile.js